import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {REMIT_BASE_URL} from './config';

export type PartnerStatus = 'active' | 'inactive';
export type PartnerRole = 'super' | 'agent';

export interface Partner {
  id: string;
  name: string;
  code: string;
  status: PartnerStatus;
  role: PartnerRole;
  parentId?: string;
  commissionPerTxnTWD: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: string;
}

export interface CreatePartnerRequest {
  name: string;
  code: string;
  commissionPerTxnTWD: string;
  status?: PartnerStatus;
  role?: PartnerRole; // defaults to 'super' on backend if omitted
  id?: string; // optional: server may generate
  parentId?: string; // required if role === 'agent'
}

export interface UpdatePartnerRequest {
  name: string;
  code: string;
  status: PartnerStatus;
  role: PartnerRole;
  parentId?: string; // required if role === 'agent'
  commissionPerTxnTWD: string;
}

export interface PartnerResponse {
  partner: Partner;
}

export interface PartnersQueryResponse {
  partners: Partner[];
  count: number;
}

export interface AgentFee {
  id: string;
  agentId: string;
  rate: string;
  createdAt: string;
  createdBy: string;
  version: string;
}

export interface SetAgentFeeRequest {
  rate: string;
}

export interface SetAgentFeeResponse {
  message: string;
}

export interface AgentFeeResponse {
  message: string;
  body: AgentFee;
}

export interface AgentFeesQueryResponse {
  message: string;
  body: AgentFee[];
  totalData: number;
}

export interface PaginationSortParams {
  pageNumber?: number | null;
  pageSize?: number | null;
  sortBy?: string | null; // defaults to createdAt on backend
  sortDirection?: number | null; // 1 asc, -1 desc; default -1
}

export type AgentFeesQueryRequest = PaginationSortParams & {
  agentId?: string | null;
  agentIds?: string[] | null;
};

export type SuperPartnersQuery = PaginationSortParams & {
  code?: string | null; // exact match; auto-uppercased server-side
  status?: PartnerStatus | null;
};

export type AgentsQuery = PaginationSortParams & {
  parentId?: string | null;
  status?: PartnerStatus | null;
  code?: string | null; // exact match
};

@Injectable({providedIn: 'root'})
export class RemitAgencyPartnerService {
  private readonly base: string;
  private readonly staffBase: string;

  constructor(
    private http: HttpClient,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) {
    this.base = `${this.remitApiUrl}/staff/agency`;
    this.staffBase = `${this.remitApiUrl}/staff`;
  }

  createPartner(payload: CreatePartnerRequest): Observable<PartnerResponse> {
    return this.http.post<PartnerResponse>(
        `${this.base}/partners`,
        this.normalizeCreatePayload(payload),
    );
  }

  getPartnerById(id: string): Observable<PartnerResponse> {
    return this.http.get<PartnerResponse>(`${this.base}/partners/${id}`);
  }

  querySuperPartners(params: SuperPartnersQuery): Observable<PartnersQueryResponse> {
    return this.http.post<PartnersQueryResponse>(
        `${this.base}/partners/query`,
        params ?? {},
    );
  }

  queryAgents(params: AgentsQuery): Observable<PartnersQueryResponse> {
    return this.http.post<PartnersQueryResponse>(
        `${this.base}/agents/query`,
        params ?? {},
    );
  }

  updatePartner(id: string, payload: UpdatePartnerRequest): Observable<PartnerResponse> {
    return this.http.put<PartnerResponse>(
        `${this.base}/partners/${id}`,
        this.normalizeUpdatePayload(payload),
    );
  }

  setAgentFee(agentId: string, payload: SetAgentFeeRequest):
   Observable<SetAgentFeeResponse> {
    return this.http.post<SetAgentFeeResponse>(
        `${this.staffBase}/agent-fee/${agentId}`,
        payload,
    );
  }

  queryAgentFees(payload: AgentFeesQueryRequest): Observable<AgentFeesQueryResponse> {
    return this.http.post<AgentFeesQueryResponse>(
        `${this.staffBase}/agent-fees`,
        payload ?? {},
    );
  }

  getLatestAgentFee(agentId: string): Observable<AgentFeeResponse> {
    return this.http.get<AgentFeeResponse>(
        `${this.staffBase}/agent-fee/${agentId}/latest`,
    );
  }

  private normalizeCreatePayload(payload: CreatePartnerRequest): CreatePartnerRequest {
    // Ensure code is uppercase before sending; server also enforces
    const code = payload.code ? payload.code.toUpperCase() : payload.code;
    return {...payload, code};
  }

  private normalizeUpdatePayload(payload: UpdatePartnerRequest): UpdatePartnerRequest {
    const code = payload.code ? payload.code.toUpperCase() : payload.code;
    // If role toggled to 'super', ensure parentId is not sent as empty string
    const parentId = payload.role === 'super' ? undefined : payload.parentId;
    return {...payload, code, parentId} as UpdatePartnerRequest;
  }
}
